import React, { useState } from 'react';
import {
  Avatar,
  Button,
  CircularProgress,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { Link, useHistory } from 'react-router-dom';
import { useCustomer } from '../../../contexts/CustomerContext';
import { useSignInDialog } from '../../auth/SignInDialog';

// Sign-in entry point in the header. Two visual states:
//   * Signed out → a "Sign in" pill button that pops the Google chooser.
//   * Signed in  → avatar (photo or initial) opening a menu with
//                  Account / Orders / Sign out.
//
// We deliberately don't gate any storefront pages on auth — login is
// optional for browsing and guest checkout, and the menu is just shortcuts.
const SignInButton: React.FC = () => {
  const { user, customer, loading, signOutCustomer } = useCustomer();
  const { openSignIn } = useSignInDialog();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const history = useHistory();

  if (loading) {
    return <CircularProgress size={20} sx={{ color: 'inherit', mx: 1 }} />;
  }

  if (!user) {
    return (
      <Button
        onClick={openSignIn}
        startIcon={<LoginIcon />}
        size="small"
        sx={{
          color: 'inherit',
          textTransform: 'none',
          ml: 1,
          fontWeight: 500,
          letterSpacing: 0.2,
        }}
      >
        Sign in
      </Button>
    );
  }

  const open = Boolean(anchorEl);
  const closeMenu = () => setAnchorEl(null);
  const photo = user.photoURL ?? undefined;
  const initial =
    (customer?.display_name ?? user.displayName ?? user.email ?? '?')
      .trim()
      .charAt(0)
      .toUpperCase();

  const handleSignOut = async () => {
    closeMenu();
    await signOutCustomer();
  };

  return (
    <>
      <Tooltip title={customer?.email ?? user.email ?? 'Account'}>
        <IconButton
          onClick={(e) => setAnchorEl(e.currentTarget)}
          size="small"
          sx={{ ml: 1, p: 0.5 }}
        >
          <Avatar src={photo} sx={{ width: 32, height: 32, fontSize: 14 }}>
            {initial}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={closeMenu}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem
          component={Link}
          to="/account"
          onClick={closeMenu}
        >
          <ListItemIcon><PersonIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Account" />
        </MenuItem>
        <MenuItem
          onClick={() => {
            closeMenu();
            history.push('/account/orders');
          }}
        >
          <ListItemIcon><ReceiptLongIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Order history" />
        </MenuItem>
        <MenuItem onClick={handleSignOut}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary="Sign out" />
        </MenuItem>
      </Menu>
    </>
  );
};

export default SignInButton;
